import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

import { AuthProvider } from "./context/AuthContext";
import { SiteProvider } from "./context/SiteContext";
import { CustomerProvider } from "./context/CustomerContext";
import { TrolleyProvider } from "./context/TrolleyContext";

import PublicLayout from "./components/PublicLayout";
import AccountAuthCallback from "./components/AccountAuthCallback";
import Home from "./pages/Home";
import Workshop from "./pages/Workshop";
import BloomClub from "./pages/BloomClub";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import TrackOrder from "./pages/TrackOrder";
import BloomId from "./pages/BloomId";
import PaymentResult from "./pages/PaymentResult";
import SupplyRoom from "./pages/SupplyRoom";
import Account from "./pages/Account";
import AccountLogin from "./pages/AccountLogin";
import AccountReset from "./pages/AccountReset";

import ConsoleLogin from "./pages/console/ConsoleLogin";
import ConsoleLayout from "./pages/console/ConsoleLayout";
import ConsoleDashboard from "./pages/console/ConsoleDashboard";
import Orders from "./pages/console/Orders";
import Enquiries from "./pages/console/Enquiries";
import CustomerDatabase from "./pages/console/CustomerDatabase";
import PrintQueue from "./pages/console/PrintQueue";
import HeritageOrders from "./pages/console/HeritageOrders";
import Varieties from "./pages/console/Varieties";
import Reports from "./pages/console/Reports";
import Settings from "./pages/console/Settings";
import Inventory from "./pages/console/Inventory";
import PackingAssistant from "./pages/console/PackingAssistant";
import WorkshopManager from "./pages/console/WorkshopManager";
import GalleryManager from "./pages/console/GalleryManager";
import SiteEditor from "./pages/console/SiteEditor";
import BloomIdAdmin from "./pages/console/BloomId";
import Vouchers from "./pages/console/Vouchers";
import SupplyManager from "./pages/console/SupplyManager";

import "./App.css";

const withLayout = (Component) => (
  <PublicLayout>
    <Component />
  </PublicLayout>
);

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AccountAuthCallback />;
  return (
    <Routes>
      <Route path="/" element={withLayout(Home)} />
      <Route path="/workshop" element={withLayout(Workshop)} />
      <Route path="/supply-room" element={withLayout(SupplyRoom)} />
      <Route path="/bloom-club" element={withLayout(BloomClub)} />
      <Route path="/gallery" element={withLayout(Gallery)} />
      <Route path="/contact" element={withLayout(Contact)} />
      <Route path="/terms" element={withLayout(Terms)} />
      <Route path="/track" element={withLayout(TrackOrder)} />
      <Route path="/bloom-id" element={withLayout(BloomId)} />
      <Route path="/payment/success" element={withLayout(PaymentResult)} />
      <Route path="/payment/cancel" element={withLayout(PaymentResult)} />
      <Route path="/account" element={withLayout(Account)} />
      <Route path="/account/login" element={withLayout(AccountLogin)} />
      <Route path="/account/reset" element={withLayout(AccountReset)} />

      <Route path="/console/login" element={<ConsoleLogin />} />
      <Route path="/console" element={<ConsoleLayout />}>
        <Route index element={<ConsoleDashboard />} />
        <Route path="orders" element={<Orders />} />
        <Route path="supply" element={<SupplyManager />} />
        <Route path="print-queue" element={<PrintQueue />} />
        <Route path="heritage-orders" element={<HeritageOrders />} />
        <Route path="varieties" element={<Varieties />} />
        <Route path="packing" element={<PackingAssistant />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="customers" element={<CustomerDatabase />} />
        <Route path="reports" element={<Reports />} />
        <Route path="bloom-id" element={<BloomIdAdmin />} />
        <Route path="vouchers" element={<Vouchers />} />
        <Route path="workshop" element={<WorkshopManager />} />
        <Route path="gallery" element={<GalleryManager />} />
        <Route path="editor" element={<SiteEditor />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App bg-cream">
      <AuthProvider>
        <SiteProvider>
          <CustomerProvider>
            <TrolleyProvider>
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
              <Toaster position="top-center" richColors />
            </TrolleyProvider>
          </CustomerProvider>
        </SiteProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
